import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  TaskInstance,
  CompleteInstanceRequest,
  useMarkAsNonApplicableInstance,
} from "@/lib/api/checklist";
import { toast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { CalendarIcon, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { TimePicker } from "@/components/ui/time-picker";

interface CompleteInstanceModalProps {
  open: boolean;
  onClose: () => void;
  instance: TaskInstance;
  allowCustomTime?: boolean;
}

export function NonApplInstanceModal({
  open,
  onClose,
  instance,
  allowCustomTime,
}: CompleteInstanceModalProps) {
  const completeInstance = useMarkAsNonApplicableInstance(instance.id);
  const [useCustomTime, setUseCustomTime] = useState(false);

  const form = useForm<CompleteInstanceRequest>({
    resolver: zodResolver(CompleteInstanceRequest),
    defaultValues: {
      completed_at: undefined,
    },
  });

  const onSubmit = async (data: CompleteInstanceRequest) => {
    try {
      const result = await completeInstance.mutateAsync(
        useCustomTime && data.completed_at
          ? { completed_at: new Date(data.completed_at).toISOString() }
          : {}
      );
      if (!result?.success) {
        throw new Error(result?.message || "Failed to mark task");
      }
      toast({
        title: "Task non-applicable",
        description: "The task instance has been marked as Non-Applicable.",
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to mark task instance.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl">Mark as Non-Applicable Task</DialogTitle>
          <DialogDescription className="text-base">
            Mark "{instance.task?.title}" as non-applicable
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
            {allowCustomTime && (
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="use-custom-time"
                  checked={useCustomTime}
                  onChange={(e) => setUseCustomTime(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div className="flex-1 space-y-1">
                  <label
                    htmlFor="use-custom-time"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Use custom completion time
                  </label>
                  <p className="text-sm text-muted-foreground">
                    By default, the task will be marked as non-applicable now
                  </p>
                </div>
              </div>
            </div>
            )}

            {useCustomTime && (
              <FormField
                control={form.control}
                name="completed_at"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-sm font-medium">
                      Completion Date & Time
                    </FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal h-10",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? (
                              format(new Date(field.value), "PPP 'at' p")
                            ) : (
                              <span>Select date and time</span>
                            )}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={
                            field.value ? new Date(field.value) : undefined
                          }
                          onSelect={(date) => {
                            if (date) {
                              const currentTime = field.value
                                ? new Date(field.value)
                                : new Date();
                              date.setHours(currentTime.getHours());
                              date.setMinutes(currentTime.getMinutes());
                              field.onChange(date.toISOString());
                            }
                          }}
                          disabled={(date) => date > new Date()}
                          initialFocus
                        />
                        <div className="p-3 border-t border-border">
                          <TimePicker
                            value={
                              field.value ? new Date(field.value) : new Date()
                            }
                            onChange={(time) => {
                              if (time && field.value) {
                                const date = new Date(field.value);
                                date.setHours(time.getHours());
                                date.setMinutes(time.getMinutes());
                                field.onChange(date.toISOString());
                              }
                            }}
                          />
                        </div>
                      </PopoverContent>
                    </Popover>
                    <FormDescription className="text-xs">
                      When the task was actually completed
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {instance.task?.recurrence_pattern && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/30 p-4">
                <p className="text-sm text-blue-900 dark:text-blue-200">
                  <strong className="font-semibold">Recurring Task:</strong> Completing
                  this will automatically create the next instance based on the recurrence pattern.
                </p>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={completeInstance.isLoading}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {completeInstance.isLoading
                  ? "Completing..."
                  : "Complete Task"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
